import { Injectable, inject } from '@angular/core';
import { ConfigService } from '@ng-darwin/config';

type TealiumErrorInput = {
  category: string;
  action: 'error' | 'warning';
  data: {
    cod_error: string | number;
    desc_error: string;
  };
};

type TealiumPageInput = {
  page_name: string;
  page_title: string;
  page_type: string;
  page_subtype?: string;
  page_category: string;
  product_scope: string;
  product_category: string;
  product_name: string | string[];
  section_name?: string;
  section_name_detail?: string;
  added_bank?: string;
  site_hierarchy?: string;
};

type TealiumConsumoOverrides = {
  product_name: string;
  product_category: string;
  product_subcategory: string;
};

/**
 * @description providedIn
 * @class TealiumService
 */
@Injectable({
  providedIn: 'root',
})
export class TealiumService {
  private _configService = inject(ConfigService);
  private _isOrigenConsumo = false;

  /**
   * @description Creates an instance of TealiumService.
   */
  constructor() {
    this._isOrigenConsumo = this._getOrigenFromSession() === 'consumo';
  }

  /**
   * @description get origen from session storage
   * @returns string
   */
  private _getOrigenFromSession(): string {
    try {
      return sessionStorage.getItem('origin') || '';
    } catch {
      return '';
    }
  }

  /**
   * @description get consumo tracking overrides
   * @returns Partial<TealiumConsumoOverrides> | Record<string, never>
   */
  private _getConsumoOverrides():
    | Partial<TealiumConsumoOverrides>
    | Record<string, never> {
    const overrides: Partial<TealiumConsumoOverrides> = {};
    const productName = sessionStorage.getItem('productName');
    const productCategory = sessionStorage.getItem('productCategory');
    const productSubcategory = sessionStorage.getItem('productSubcategory');

    if (productName) { overrides.product_name = productName };
    if (productCategory) { overrides.product_category = productCategory };
    if (productSubcategory) { overrides.product_subcategory = productSubcategory };

    if (Object.keys(overrides).length > 0) {
      return overrides;
    }

    this._isOrigenConsumo = this._getOrigenFromSession() === 'consumo';
    if (!this._isOrigenConsumo) {
      return {};
    }
    return {
      product_name: 'prestamo consumo',
      product_category: 'loans',
      product_subcategory: 'preautorizados',
    };
  }

  /**
   * @description load
   */
  load(): void {
    this._configUtagjs();
  }

  /**
   * @description configUtagjs
   */
  private _configUtagjs(): void {
    const tealium = this._configService.config.app['tealium'] as any;
    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `//tags.tiqcdn.com/utag/${tealium.account}/${tealium.profile}/${tealium.environment}/utag.js`;
    document.body.appendChild(script);
    const scriptsync: HTMLScriptElement = document.createElement('script');
    scriptsync.type = 'text/javascript';
    scriptsync.src = `//tags.tiqcdn.com/utag/${tealium.account}/${tealium.profile}/${tealium.environment}/utag.sync.js`;
    document.head.appendChild(scriptsync);
    if (!(window as any).utag_data) {
      (window as any).utag_data = {};
    }
    (window as any).utag_data.asset_origin = 'homebanking'; // Indica el nombre del aplicativo que ha originado que se cargue la página
    (window as any).utag_data.access_type = 'privado'; // Indicará si el acceso es público; privado o interno para empleados**
    (window as any).utag_data.entity = 'Santander'; // Indica el nombre de la entidad de la web**
    (window as any).utag_data.site_id = 'sanes-bancaonline'; // Indica un identificador del sitio web; para distinguir las diferentes areas dentro de la web comercial**
    (window as any).utag_data.customer_segment = 'RET'; // indentifica el segmento del cliente SELECT; SPB; RET
    (window as any).utag_data.user_type = 'cliente'; // Indica si el visitante es cliente o empleado
    (window as any).utag_data.client = '1'; // Indica si el usuario es cliente identificado (1)
    (window as any).utag_data.structural_segment = 'XXX'; // Indica el tipo de cliente en conjunto con el segmento  estructural
    (window as any).utag_data.commercial_segment = 'YYY'; // Indica el tipo de cliente en conjunto al segmento comercial
  }

  /**
   * @description sendPageLoad
   * @param input TealiumPageInput
   */
  sendPageLoad(input: TealiumPageInput): void {
    const payload = {
      ...input,
      ...this._getConsumoOverrides(),
      added_bank: (window as any)?.utag_data?.added_bank || '',
    };
    (window as any).$('body').trigger('evShowView', payload);
  }

  /**
   * @description sendError
   * @param input TealiumErrorInput
   */
  sendError(input: TealiumErrorInput): void {
    (window as any).$('body').trigger('evSendAction', input);
  }

  /**
   * @description sendError
   * @param input TealiumErrorInput
   */
  sendEvent(input: any): void {
    const payload =
      input &&
        typeof input === 'object' &&
        input.data &&
        typeof input.data === 'object'
        ? {
          ...input,
          data: {
            ...input.data,
            ...this._getConsumoOverrides(),
          },
        }
        : input;
    (window as any).$('body').trigger('evSendAction', payload);
  }

  /**
   * @description setAddedBank
   * @param bank string
   */
  setAddedBank(bank: string): void {
    if ((window as any)?.utag_data) {
      (window as any).utag_data.added_bank = bank;
    }
  }

  /**
   * @description setUtagValues
   * @param values any
   */
  setUtagValues(values: any): void {
    (window as any).utag_data.user_id = values.uid;
    (window as any).utag_data.cod_cliente =
      values.attributes.personNumberType + values.attributes.personNumberCode;
    (window as any).utag_data.page_lang =
      values.attributes.preferredLanguage.trim();
  }
}
